<script lang="ts" setup>
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import { useDisplay } from 'vuetify'
import EmojiPicker from 'vue3-emoji-picker'
import { installChatTrafficFetchSpy } from '@/modules/chat-matrix-fast/utils/chatTrafficFileLog'
import {
  chatDebugLog, chatPerfLog, debugConsoleError,
  debugConsoleWarn,
  searchDebugLog,
  summarizeTopicForLog,
  topicsDebugLog,
} from '@/modules/chat-matrix-fast/utils/chatDebugLog'
import 'vue3-emoji-picker/css'
import ChatLeftSidebarContent from '@/views/apps/chat-matrix-fast/components/sidebars/ChatLeftSidebarContent.vue'
import ChatLog from '@/views/apps/chat-matrix-fast/components/ChatLog.vue'
import ChatLogSkeleton from '@/views/apps/chat-matrix-fast/components/chat-log/ChatLogSkeleton.vue'
import ChatPanelSkeleton from '@/views/apps/chat-matrix-fast/components/ChatPanelSkeleton.vue'
import MessageSelectionBar from '@/views/apps/chat-matrix-fast/components/chat-log/MessageSelectionBar.vue'
import ChatInput from '@/views/apps/chat-matrix-fast/components/ChatInput.vue'
import ChatUserProfileSidebarContent from '@/views/apps/chat-matrix-fast/components/sidebars/ChatUserProfileSidebarContent.vue'
import EmployeeProfileSidebar from '@/components/createdUi/EmployeeProfileSidebar.vue'
import CreateGroupDialog from '@/views/apps/chat-matrix-fast/components/dialogs/CreateGroupDialog.vue'
import CreateTopicDialog from '@/views/apps/chat-matrix-fast/components/dialogs/CreateTopicDialog.vue'
import ManageGroupDialog from '@/views/apps/chat-matrix-fast/components/dialogs/ManageGroupDialog.vue'
import GroupInfoDialog from '@/views/apps/chat-matrix-fast/components/dialogs/GroupInfoDialog.vue'
import ConfirmChatDialog from '@/views/apps/chat-matrix-fast/components/dialogs/ConfirmChatDialog.vue'
import FilesAndMediaDialog from '@/views/apps/chat-matrix-fast/components/dialogs/FilesAndMediaDialog.vue'
import FileUploadDialog from '@/views/apps/chat-matrix-fast/components/dialogs/FileUploadDialog.vue'
import {
  avatarText,
  getAvatarColor,
  isMySender,
  resolveAvatarBadgeVariant,
} from '@/modules/chat-matrix-fast/utils'
import { useChatStore } from '@/modules/chat-matrix-fast/stores/chatViewStore'
import { useChatStore as useChatStoreCore } from '@/modules/chat-matrix-fast/stores/chatStore'
import { setSuppressAutoReadReceipt } from '@/modules/chat-matrix-fast/stores/core/runtime'
import type { ChatContact as TypeChatContact } from '@/modules/chat-matrix-fast/types'
import type { MessageWithReply } from '@/modules/chat-matrix-fast/types/messageTypes'
import { isEventChat, isGroupChat } from '@/modules/chat-matrix-fast/types/ChatMessage'
import { GROUP_INVITE_UI_ENABLED } from '@/modules/chat-matrix-fast/utils/groupInviteLink'
import {
  groupChatTypeLabel,
  isGroupLikeChatType,
  matchesChatListTab,
  resolveChatHeaderTitle,
} from '@/modules/chat-matrix-fast/constants/chat'
import {
  applyMessengerBootChatListTab,
  clearStoredSelectedChatId,
} from '@/modules/chat-matrix-fast/utils/chatListPersistence'
import type { IChat, IChatTopic } from '@/modules/chat-matrix-fast/types/IChat'
import { parseQuoteFromContent } from '@/modules/chat-matrix-fast/utils/messageFormatter'
import { useResponsiveLeftSidebar } from '@/@core/composable/useResponsiveSidebar'
import {
  buildNotificationsMenuItem,
  copyGroupInviteLink,
  copyGroupShareLink,
  useActiveChatNotifications,
  useChatComposerReady,
  useChatEmployee,
  useChatFileDrop,
  useChatModals,
  useChatScroll,
  useChatSearch,
  useChatStructurePoll,
  useChatUrlSync,
  useEmojiPicker,
  useGroupRoomAvatar,
  useMatrixTyping,
  useMentionMembers,
} from '@/modules/chat-matrix-fast/composables'
import { useChatLogStickToBottom } from '@/modules/chat-matrix-fast/composables/useChatLogStickToBottom'
import {
  chatOpenJumpLog,
  summarizeJumpScroll,
} from '@/modules/chat-matrix-fast/utils/chatOpenJumpLog'
import AuthenticatedVImg from '@/views/apps/chat-matrix-fast/components/AuthenticatedVImg.vue'
import ChatTopicsTabs from '@/views/apps/chat-matrix-fast/components/ChatTopicsTabs.vue'
import { getCurrentMatrixUserId } from '@/modules/chat-matrix-fast/services/matrixChatClient'
import {
  type ChatMentionCandidate,
  collectMentionUserIds,
  toMentionCandidate,
} from '@/modules/chat-matrix-fast/utils/mentions'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'
import MatrixAuthPrompt from '@/modules/chat-matrix-fast/components/MatrixAuthPrompt.vue'
import { formatMatrixLastSeen } from '@/modules/chat-matrix-fast/services/matrixPresenceService'
import { useReactiveNow } from '@/utils/useReactiveNow'
import {
  findGeneralTopic,
  isTopicsModeChat,
  isTopicsTabsDisplay,
  resolveTopicsDisplayMode,
  setStoredTopicsDisplayMode,
} from '@/modules/chat-matrix-fast/utils/topics'
import {
  releaseChatSurface,
  retainChatSurface,
} from '@/modules/chat-matrix-fast/utils/chatSurface'
import { clearMessageDraft, getMessageDraft } from '@/modules/chat-matrix-fast/utils/messageDrafts'
import { useMessageDraftSync } from '@/modules/chat-matrix-fast/composables/useMessageDraftSync'
import { scrollToChatMessage } from '@/modules/chat-matrix-fast/utils/scrollToChatMessage'

installChatTrafficFetchSpy()

definePage({
  meta: {
    layoutWrapperClasses: 'layout-content-height-fixed',
  },
})

const vuetifyDisplays = useDisplay()
const store = useChatStore()
const chatStoreCore = useChatStoreCore()
const activeMatrixRoomId = computed(() => {
  // В topics-режиме ищем по комнате активной темы, не по parent room.
  const topicRoomId = chatStoreCore.activeTopic?.matrix_room_id?.trim()
  if (topicRoomId) {
    return topicRoomId
  }

  const activeChatId = store.activeChat?.chat?.id
  if (!activeChatId) {
    return null
  }

  const currentChat = chatStoreCore.currentChat?.id === activeChatId
    ? chatStoreCore.currentChat
    : chatStoreCore.chats.find(chat => chat.id === activeChatId)

  return currentChat?.matrix_room_id ?? null
})

const deleteForPeer = ref(false)
const feedbackStore = useFeedbackStore()
const showChatPanelSkeleton = computed(() =>
  (feedbackStore.isGlobalChatLoading || chatStoreCore.isInitializing) && !store.activeChat,
)

const { isLeftSidebarOpen } = useResponsiveLeftSidebar(
  vuetifyDisplays.smAndDown,
)

const chatLogPS = ref<HTMLElement | null>(null)
interface ChatLogHandle {
  clearMessageSelection: () => void
  openForwardSelected: () => void
}
const chatLogRef = ref<ChatLogHandle | null>(null)
const isMessageSelecting = ref(false)
const selectedMessageCount = ref(0)

function onMessageSelectionChange(payload: { active: boolean; count: number }) {
  isMessageSelecting.value = payload.active
  selectedMessageCount.value = payload.count
}

const {
  scrollToBottom,
  isNearBottom,
} = useChatScroll(chatLogPS, computed(() => store.isLoadingChat))
const {
  beginOpenStick,
  onTimelineSignal,
  stickNow,
  shouldStick,
} = useChatLogStickToBottom(chatLogPS)

const refInputEl = ref()
const leftSidebarSearchInputRef = ref()

const isMessageSearchActive = ref(false)
const isInitialized = ref(false)

const { restoreFromUrl, syncUrl } = useChatUrlSync(isInitialized)

const { fetchEmployeeUuid } = useChatEmployee(
  computed(() => store.activeChat as any),
  computed(() => chatStoreCore.currentChat),
  computed(() => chatStoreCore.chats),
)

const { q, messageSearchResults, performSearch, clearSearch } = useChatSearch(
  isMessageSearchActive,
  {
    onSearch: (query: string) => {
      if (!(isInitialized.value || chatStoreCore.isInitialized)) {
        searchDebugLog('page · skip (not initialized)', { query })
        return
      }

      const trimmedQuery = query.trim()

      if (isMessageSearchActive.value) {
        searchDebugLog('page · message search', {
          query,
          trimmedQuery,
          roomId: activeMatrixRoomId.value,
          topicRoomId: chatStoreCore.activeTopic?.matrix_room_id ?? null,
          parentRoomId: store.activeChat?.chat && 'matrix_room_id' in store.activeChat.chat
            ? (store.activeChat.chat as { matrix_room_id?: string | null }).matrix_room_id ?? null
            : null,
          activeTopicId: chatStoreCore.activeTopic?.id ?? null,
          activeChatId: store.activeChat?.chat?.id ?? null,
          hasContact: Boolean(store.activeChat?.contact),
        })

        if (!activeMatrixRoomId.value || !store.activeChat?.contact) {
          searchDebugLog('page · message search skipped', {
            reason: !activeMatrixRoomId.value ? 'no-room-id' : 'no-contact',
            query: trimmedQuery,
          })
          return
        }

        // Короткий/пустой запрос — очищаем hits, чат/тему не трогаем.
        if (trimmedQuery.length < 2) {
          searchDebugLog('page · message search clear (query too short)', {
            length: trimmedQuery.length,
          })
          performSearch(
            '',
            activeMatrixRoomId.value,
            store.activeChat.contact,
            store.profileUser,
          )
          return
        }

        performSearch(
          trimmedQuery,
          activeMatrixRoomId.value,
          store.activeChat.contact,
          store.profileUser,
        )
        return
      }

      // Поиск по списку чатов
      if (!trimmedQuery.length) {
        // Пустое поле — показываем полный список
        store.fetchChatsAndContacts('')

        return
      }

      if (trimmedQuery.length < 2) {
        chatDebugLog('page search → cleared (query too short)', {
          queryLength: trimmedQuery.length,
        })
        // Одна буква — очищаем результаты (список не показываем)
        store.chatsContacts = []
        store.contacts = []

        return
      }

      // Нормальный запрос (2+ символа) — серверный поиск
      store.fetchChatsAndContacts(trimmedQuery)
    },
  },
)

const previousMessagesCount = ref(0)

const OLDER_HISTORY_TRIGGER_PX = 160
const OLDER_HISTORY_EMPTY_PAGE_RETRIES = 3
const isLoadingOlderHistory = ref(false)
const hasMoreOlderHistory = ref(true)
let olderHistoryGeneration = 0

const maybeFillOlderHistory = () => {
  const el = chatLogPS.value
  if (!el || store.isLoadingChat || isLoadingOlderHistory.value || !hasMoreOlderHistory.value) {
    return
  }
  if (el.scrollHeight <= el.clientHeight + OLDER_HISTORY_TRIGGER_PX) {
    loadOlderHistory()
  }
}

async function loadOlderHistory() {
  const el = chatLogPS.value
  if (!el || isLoadingOlderHistory.value || !hasMoreOlderHistory.value || store.isLoadingChat) {
    return
  }

  const generation = olderHistoryGeneration
  const chatIdAtStart = chatStoreCore.currentChat?.id ?? null
  const roomIdAtStart = chatStoreCore.activeTopic?.matrix_room_id
    ?? chatStoreCore.currentChat?.matrix_room_id
    ?? null
  const isSameOpenTarget = () => {
    const chatId = chatStoreCore.currentChat?.id ?? null
    const roomId = chatStoreCore.activeTopic?.matrix_room_id
      ?? chatStoreCore.currentChat?.matrix_room_id
      ?? null
    return chatId === chatIdAtStart && roomId === roomIdAtStart
  }
  const keepBottom = shouldStick() || isNearBottom()
  const beforeCount = chatStoreCore.messages.length
  const prevHeight = el.scrollHeight
  const prevTop = el.scrollTop
  isLoadingOlderHistory.value = true
  chatOpenJumpLog('page · loadOlderHistory start', {
    keepBottom,
    beforeCount,
    scroll: summarizeJumpScroll(el),
  })
  try {
    let result = await chatStoreCore.loadOlderMessages()
    if (!isSameOpenTarget()) {
      return
    }
    for (let attempt = 0; result.added === 0 && result.hasMore && attempt < OLDER_HISTORY_EMPTY_PAGE_RETRIES; attempt += 1) {
      result = await chatStoreCore.loadOlderMessages()
      if (!isSameOpenTarget()) {
        return
      }
    }
    hasMoreOlderHistory.value = result.hasMore

    const afterCount = chatStoreCore.messages.length
    const added = Math.max(result.added, afterCount - beforeCount)
    if (added > 0) {
      await nextTick()
      if (!isSameOpenTarget()) {
        return
      }
      el.style.scrollBehavior = 'auto'
      if (keepBottom || shouldStick()) {
        stickNow('older-history-fill')
      } else {
        el.scrollTop = el.scrollHeight - prevHeight + prevTop
      }
      chatOpenJumpLog('page · loadOlderHistory applied', {
        added,
        keepBottom,
        afterCount,
        generationStale: generation !== olderHistoryGeneration,
        scroll: summarizeJumpScroll(el),
      })
      requestAnimationFrame(() => {
        el.style.scrollBehavior = ''
      })
    }
  } catch (error) {
    debugConsoleWarn('[chat-matrix-fast] loadOlderHistory failed', error)
  } finally {
    isLoadingOlderHistory.value = false
  }
}

const onChatLogScroll = () => {
  const el = chatLogPS.value
  if (el && el.scrollTop <= OLDER_HISTORY_TRIGGER_PX) {
    loadOlderHistory()
  }
}

watch(
  () => [chatStoreCore.currentChat?.id, chatStoreCore.activeTopic?.matrix_room_id],
  () => {
    olderHistoryGeneration += 1
    hasMoreOlderHistory.value = true
  },
)

watch(
  () => chatStoreCore.messages.length,
  count => {
    if (!count) {
      return
    }
    nextTick(() => {
      maybeFillOlderHistory()
    })
  },
)

// Отслеживаем изменения списка чатов по длине массива вместо deep watch
watch(
  () => chatStoreCore.chats.length,
  () => {
    if (!isInitialized.value) {
      return
    }

    store.syncWithCoreStore()
  },
)

watch(
  () => {
    const messages = chatStoreCore.messages
    const last = messages.length > 0 ? messages[messages.length - 1] : null
    return {
      chatId: chatStoreCore.currentChat?.id ?? null,
      count: messages.length,
      lastKey: last
        ? String(last.matrix_event_id || last.id)
        : null,
    }
  },
  (next, prev) => {
    const activeViewChatId = store.activeChat?.chat?.id
    const isActiveChat = activeViewChatId != null && (
      chatStoreCore.currentChat?.id === activeViewChatId
      || Boolean(chatStoreCore.activeTopic)
    )

    if (chatStoreCore.activeTopic || chatStoreCore.showTopicsList) {
      topicsDebugLog('page · watch messages', {
        isActiveChat,
        activeViewChatId: activeViewChatId ?? null,
        coreCurrentChatId: chatStoreCore.currentChat?.id ?? null,
        coreMessagesCount: next.count,
        uiMessagesCount: chatStoreCore.messages.length,
        activeTopic: summarizeTopicForLog(chatStoreCore.activeTopic),
      })
    }

    if (!isActiveChat || store.isLoadingChat) {
      previousMessagesCount.value = next.count
      return
    }

    if (isLoadingOlderHistory.value) {
      previousMessagesCount.value = next.count
      return
    }

    nextTick(() => {
      onTimelineSignal(next, prev)
    })
    previousMessagesCount.value = next.count
  },
)

// Убрано дублирование автоскролла - теперь только один watch для chatStoreCore.messages

watch(
  () => chatStoreCore.activeTopic?.id,
  (topicId, prevTopicId) => {
    topicsDebugLog('page · watch activeTopic', {
      topicId: topicId ?? null,
      prevTopicId: prevTopicId ?? null,
      topic: summarizeTopicForLog(chatStoreCore.activeTopic),
      uiActiveChatId: store.activeChat?.chat?.id ?? null,
    })

    if (chatStoreCore.activeTopic && store.activeChat?.chat?.id) {
      store.syncWithCoreStore()
    }
  },
)

watch(
  () => chatStoreCore.currentChat?.id,
  (currentChatId, prevChatId) => {
    if (chatStoreCore.activeTopic || chatStoreCore.showTopicsList) {
      topicsDebugLog('page · watch currentChat', {
        currentChatId: currentChatId ?? null,
        prevChatId: prevChatId ?? null,
        uiActiveChatId: store.activeChat?.chat?.id ?? null,
        showTopicsList: chatStoreCore.showTopicsList,
      })
    }

    if (
      chatStoreCore.activeTopic
      && currentChatId
      && store.activeChat?.chat?.id === currentChatId
    ) {
      store.syncWithCoreStore()
    }
  },
)

watch(
  () => store.activeChat,
  (newActiveChat, oldActiveChat) => {
    if (newActiveChat) {
      const isNewChat = !oldActiveChat || newActiveChat.chat?.id !== oldActiveChat.chat?.id

      if (isNewChat) {
        previousMessagesCount.value = newActiveChat.chat?.messages?.length || 0
        nextTick(() => {
          beginOpenStick()
          const fillGeneration = olderHistoryGeneration
          // Live sync often has only a short tail; paginate soon after first paint.
          window.setTimeout(() => {
            if (fillGeneration !== olderHistoryGeneration) {
              return
            }
            maybeFillOlderHistory()
          }, 250)
        })
      }
    }

    if (vuetifyDisplays.mdAndUp.value && !newActiveChat && oldActiveChat !== undefined) {
      isLeftSidebarOpen.value = true
    }
  },
)

const closeLeftSidebar = () => {
  isLeftSidebarOpen.value = false
}

const isMobileChatListMode = computed(() => {
  if (!vuetifyDisplays.smAndDown.value) {
    return false
  }

  if (!store.activeChat) {
    return true
  }

  // Группа с темами: на мобилке список тем остаётся в ChatLeftSidebarContent
  return chatStoreCore.showTopicsList && !chatStoreCore.activeTopic
})

const backToMobileChatList = () => {
  if (chatStoreCore.activeTopic) {
    chatStoreCore.backToTopicsList()
    return
  }

  const chatId = store.activeChat?.chat?.id
  if (chatId != null) {
    clearStoredSelectedChatId(chatId)
  }

  store.activeChat = null
  chatStoreCore.currentChat = null

  if (
    chatStoreCore.showTopicsList
    || chatStoreCore.activeTopic
    || chatStoreCore.selectedTopicsGroupId != null
  ) {
    chatStoreCore.closeTopicsNavigation()
  }
}

const handleChatReadedEvent = (event: Event) => {
  const customEvent = event as CustomEvent
  const eventChatId = customEvent.detail?.chatId

  if (eventChatId && store.activeChat?.chat?.id === eventChatId) {
    store.syncWithCoreStore()
  }
}

const restoreSavedChat = async () => {
  const restoredFromUrl = await restoreFromUrl()

  if (restoredFromUrl) {
    // Desktop: список чатов всегда виден (permanent drawer)
    if (vuetifyDisplays.mdAndUp.value) {
      isLeftSidebarOpen.value = true
    }

    const openedId = store.activeChat?.chat?.id
    if (openedId) {
      try {
        localStorage.setItem('selectedChatId', String(openedId))
      } catch {
        // ignore
      }
    }

    nextTick(() => scrollToBottom({ behavior: 'auto' }))

    return
  }

  // Без ?tab= в URL остаёмся на дефолте (обычные чаты). Не восстанавливаем
  // events из localStorage — иначе вход из меню на /messenger уводит на задачи.

  const savedChatId = localStorage.getItem('selectedChatId')

  if (!savedChatId) {
    if (vuetifyDisplays.smAndDown.value) {
      isLeftSidebarOpen.value = true
    }

    return
  }

  const chatId = Number.parseInt(savedChatId, 10)

  if (Number.isNaN(chatId)) {
    return
  }

  const openRestoredChat = async (chat: IChat) => {
    if (!matchesChatListTab(chat, store.chatListTab)) {
      // Stale id от другой вкладки не должен переключать Чаты → НС.
      clearStoredSelectedChatId(chatId)
      if (vuetifyDisplays.smAndDown.value) {
        isLeftSidebarOpen.value = true
      }

      return
    }

    setSuppressAutoReadReceipt(true)
    try {
      await store.getChat(chatId)
    } finally {
      setSuppressAutoReadReceipt(false)
    }
    nextTick(() => scrollToBottom({ behavior: 'auto' }))
  }

  const chat = chatStoreCore.chats.find(c => c.id === chatId)

  if (chat) {
    await openRestoredChat(chat)

    return
  }

  if (!chatStoreCore.isInitialized) {
    return
  }

  try {
    const fetchedChat = await chatStoreCore.fetchChat(chatId)

    if (fetchedChat) {
      if (!matchesChatListTab(fetchedChat, store.chatListTab)) {
        clearStoredSelectedChatId(chatId)
        if (vuetifyDisplays.smAndDown.value) {
          isLeftSidebarOpen.value = true
        }

        return
      }

      if (!chatStoreCore.chats.find(c => c.id === chatId)) {
        chatStoreCore.chats.push(fetchedChat)
        await store.fetchChatsAndContacts('')
      }
      setSuppressAutoReadReceipt(true)
      try {
        await store.getChat(chatId)
      } finally {
        setSuppressAutoReadReceipt(false)
      }
      nextTick(() => scrollToBottom({ behavior: 'auto' }))

      return
    }
  } catch (error) {
    chatDebugLog('restoreSavedChat → stale selectedChatId cleared', {
      chatId,
      error: error instanceof Error ? error.message : String(error),
    })
  }

  clearStoredSelectedChatId(chatId)

  if (vuetifyDisplays.smAndDown.value) {
    isLeftSidebarOpen.value = true
  }
}

const emojiBtnRef = ref<HTMLElement | null>(null)
const messageInputRef = ref<any>(null)

const emojiPicker = useEmojiPicker(
  emojiBtnRef,
  messageInputRef,
  { isMobile: vuetifyDisplays.smAndDown },
)

const msg = ref('')

const selectEmoji = (emoji: any) => {
  emojiPicker.onSelectEmoji(emoji, msg)
}

const modals = useChatModals()

const isGroupOrChannel = computed(() => {
  if (!store.activeChat?.chat) {
    return false
  }

  return isGroupLikeChatType(store.activeChat.chat.type)
})

const isEventChatActive = computed(() => store.isEventChat())

const activeEventChat = computed(() => {
  const chat = store.activeChat?.chat
  return chat && isEventChat(chat) ? chat : null
})

const openEventUrl = () => {
  const url = activeEventChat.value?.event_url
  if (url) {
    window.open(url, '_blank', 'noopener,noreferrer')
  }
}

const updateEmojiPickerPosition = () => {
  if (emojiPicker.showEmojiPicker.value) {
    emojiPicker.calculatePosition()
  }
}

onMounted(async () => {
  retainChatSurface()
  window.addEventListener('chat-readed', handleChatReadedEvent)
  document.addEventListener('click', emojiPicker.onDocumentClick, { passive: true })
  window.addEventListener('resize', updateEmojiPickerPosition)
  window.addEventListener('scroll', updateEmojiPickerPosition, { passive: true })

  try {
    chatPerfLog('page · onMounted start')
    chatDebugLog('page onMounted → start')
    store.ensureProfileUser()
    applyMessengerBootChatListTab({ core: chatStoreCore, view: store })
    const wasInitialized = chatStoreCore.isInitialized
    const ready = await chatStoreCore.initializeOnce()
    chatPerfLog('page · initializeOnce done', { ready })
    if (!ready && chatStoreCore.needsMatrixAuth) {
      chatDebugLog('page onMounted → stop (needs Matrix auth)')
      return
    }

    if (ready && wasInitialized) {
      await chatStoreCore.fetchChats(undefined, { silent: true })
    }
    await store.fetchChatsAndContacts('')
    isInitialized.value = true
    chatPerfLog('page · fetchChatsAndContacts done (list, превью ещё могут грузиться)', {
      chatsCount: chatStoreCore.chats.length,
    })
    chatDebugLog('page onMounted → ready', {
      coreChatsCount: chatStoreCore.chats.length,
      uiChatsContactsCount: store.chatsContacts.length,
      chatListTab: store.chatListTab,
    })
    await nextTick()
    await restoreSavedChat()
    syncUrl()
    chatPerfLog('page · boot complete (list + restoreSavedChat)')
  } catch (error) {
    debugConsoleError(error)
  }
})

onUnmounted(() => {
  releaseChatSurface()
  document.removeEventListener('click', emojiPicker.onDocumentClick)
  window.removeEventListener('resize', updateEmojiPickerPosition)
  window.removeEventListener('scroll', updateEmojiPickerPosition)
  window.removeEventListener('chat-readed', handleChatReadedEvent)
})

const activeCoreChat = computed(() => {
  const activeChatId = store.activeChat?.chat?.id
  if (!activeChatId) {
    return null
  }
  if (chatStoreCore.currentChat?.id === activeChatId) {
    return chatStoreCore.currentChat
  }
  return chatStoreCore.chats.find(chat => chat.id === activeChatId) ?? null
})

const {
  canCompose,
  showComposer,
  readyMessage,
  readyStatus,
  showTimeline,
  retryConnection,
  retrying,
  canRetryConnection,
} = useChatComposerReady({
  chat: activeCoreChat,
  activeTopic: computed(() => chatStoreCore.activeTopic),
  showTopicsList: computed(() => chatStoreCore.showTopicsList),
})

/** Композер скрыт / баннер — пока чат не ready (creating/joining/failed). */
const isComposerBlocked = computed(() =>
  Boolean(store.activeChat?.chat?.id) && !canCompose.value,
)

useChatStructurePoll({
  chatId: computed(() => store.activeChat?.chat?.id),
  blocked: isComposerBlocked,
  readyStatus,
  canCompose,
  onRoomReady: async chatId => {
    if (
      Number(store.activeChat?.chat?.id) === Number(chatId)
      && Number(chatStoreCore.currentChat?.id) === Number(chatId)
    ) {
      return
    }
    await store.getChat(chatId)
  },
})

const {
  typingText,
  markTyping,
  stopTyping,
} = useMatrixTyping(activeMatrixRoomId)

const mentionChatId = computed(() => store.activeChat?.chat?.id)
const mentionRoomId = computed(() => activeMatrixRoomId.value ?? undefined)
const {
  members: mentionMembers,
  ensureMentionMembers,
} = useMentionMembers({
  chatId: mentionChatId,
  matrixRoomId: mentionRoomId,
})

const mentionCandidates = computed(() =>
  mentionMembers.value
    .map(toMentionCandidate)
    .filter((item): item is ChatMentionCandidate => Boolean(item)),
)

const excludeMentionKey = computed(() => getCurrentMatrixUserId())

const replyTo = ref<{
  id: number | string
  message: any
  senderId: number | string
  senderName: string
} | null>(null)

const editingMessage = ref<{
  id: string | number
  message: MessageWithReply
  originalText: string
} | null>(null)

const isSendingMessage = ref(false)

useMessageDraftSync({
  msg,
  getChatId: () => store.activeChat?.chat?.id,
  isEditing: () => Boolean(editingMessage.value),
  onLeaveChat: () => {
    editingMessage.value = null
    replyTo.value = null
  },
})

watch(
  msg,
  value => {
    if (value.trim()) {
      markTyping()
    } else {
      stopTyping()
    }
  },
)

const handleStartEdit = (messageId: string | number, msgData: MessageWithReply) => {
  const rawText = (msgData.message ?? msgData.content) || ''
  const { remainingText } = parseQuoteFromContent(rawText)
  const originalText = remainingText || rawText

  editingMessage.value = { id: messageId, message: msgData, originalText }
  replyTo.value = null
  msg.value = originalText
  nextTick(() => {
    messageInputRef.value?.focus?.()
  })
}

const cancelEdit = () => {
  editingMessage.value = null
  msg.value = getMessageDraft(store.activeChat?.chat?.id)
}

async function sendMessage(mentionUserIds?: string[]) {
  if (!store.activeChat || isSendingMessage.value) {
    return
  }

  if (editingMessage.value) {
    const content = msg.value?.trim()
    if (!content) {
      return
    }
    const chatId = store.activeChat.chat?.id
    if (!chatId) {
      return
    }
    const msgId = editingMessage.value.id
    if (msgId == null || msgId === '') {
      return
    }

    isSendingMessage.value = true
    const editState = editingMessage.value
    editingMessage.value = null
    msg.value = getMessageDraft(chatId)

    try {
      await chatStoreCore.updateMessage(
        chatId,
        msgId,
        content,
        editState.message?.matrix_event_id,
      )
      stopTyping()
      nextTick(() => scrollToBottom({ behavior: 'auto' }))
    } catch (error) {
      msg.value = content
      editingMessage.value = editState
      debugConsoleError('Ошибка при сохранении сообщения:', error)
    } finally {
      isSendingMessage.value = false
    }

    return
  }

  if (!msg.value?.trim()) {
    return
  }

  const content = msg.value.trim()
  const replyToId = replyTo.value?.id
  const replyToMessage = replyTo.value?.message
  const replySenderName = replyTo.value?.senderName
  const chatId = store.activeChat.chat?.id

  isSendingMessage.value = true
  msg.value = ''
  replyTo.value = null
  clearMessageDraft(chatId)

  try {
    let replyPayload = replyToMessage
    if (replyToMessage && replySenderName) {
      replyPayload = { ...replyToMessage, senderName: replySenderName }
    }

    await store.sendMsg(
      content,
      replyToId,
      replyPayload,
      mentionUserIds?.length
        ? mentionUserIds
        : collectMentionUserIds(content, mentionCandidates.value),
    )

    stopTyping()
    nextTick(() => scrollToBottom({ behavior: 'auto' }))
  } catch (error) {
    msg.value = content
    debugConsoleError('Ошибка при отправке сообщения:', error)
  } finally {
    isSendingMessage.value = false
  }
}

const handleReply = (message: MessageWithReply, senderId: number | string) => {
  // Получаем имя отправителя из разных возможных источников
  const senderName
    = (message as any).created_by?.full_name
    || (message as any).created_by?.user_name
    || (message as any).author_name
    || (message as any).name
    || (isMySender(senderId, store.profileUser?.id || '', getCurrentMatrixUserId())
      ? store.profileUser?.fullName || 'Я'
      : store.activeChat?.contact?.fullName || 'Неизвестный')

  replyTo.value = {
    id: message.id || `${senderId}-${message.time}`,
    message: message as any,
    senderId,
    senderName,
  }

  nextTick(() => {
    messageInputRef.value?.focus?.()
  })
}

const cancelReply = () => {
  replyTo.value = null
}

const openChatOfContact = async (
  userId: TypeChatContact['id'],
  options?: { preferTopicsList?: boolean },
) => {
  topicsDebugLog('page · openChatOfContact · start', {
    userId,
    uiActiveChatId: store.activeChat?.chat?.id ?? null,
    coreCurrentChatId: chatStoreCore.currentChat?.id ?? null,
    activeTopic: summarizeTopicForLog(chatStoreCore.activeTopic),
    preferTopicsList: options?.preferTopicsList ?? false,
  })

  // Desktop: список в permanent drawer — не закрываем.
  // Mobile: список/чат переключаются через isMobileChatListMode (activeChat), без drawer.

  try {
    await store.getChat(userId, options)

    topicsDebugLog('page · openChatOfContact · done', {
      userId,
      uiActiveChatId: store.activeChat?.chat?.id ?? null,
      coreCurrentChatId: chatStoreCore.currentChat?.id ?? null,
      uiMessagesCount: chatStoreCore.messages.length,
      activeTopic: summarizeTopicForLog(chatStoreCore.activeTopic),
      showTopicsList: chatStoreCore.showTopicsList,
    })

    try {
      localStorage.setItem('selectedChatId', String(userId))
    } catch (error) {
      debugConsoleError('Ошибка при сохранении в localStorage:', error)
    }

    nextTick(() => scrollToBottom({ behavior: 'auto' }))
  } catch (error) {
    topicsDebugLog('page · openChatOfContact · error', {
      userId,
      error: String(error),
    })
    debugConsoleError('Ошибка при открытии чата:', error)

    // Можно добавить уведомление пользователю
  }
}

const deleteChat = () => {
  if (!store.activeChat?.chat) {
    return
  }
  if (isEventChatActive.value) {
    return
  }

  const isGroup = store.isGroupOrChannel()

  if (isGroup && !store.canDeleteActiveChat()) {
    return
  }

  deleteForPeer.value = false
  modals.openDeleteChatDialog()
}

const isActiveDialogChat = computed(() => {
  const type = store.activeChat?.chat?.type
  return type === 'dialog' || type === 'direct'
})

const presenceNow = useReactiveNow()

/** Подпись под именем в диалоге: печатает | должность (в сети|был(а) …) */
const dialogStatusLabel = computed(() => {
  if (!isActiveDialogChat.value || !store.activeChat?.contact) {
    return ''
  }

  if (typingText.value) {
    return typingText.value
  }

  const role = store.activeChat.contact.role?.trim() || ''
  const presence = store.activeChat.contact.online
    ? 'в сети'
    : formatMatrixLastSeen(
      chatStoreCore.currentChat?.id === store.activeChat.chat?.id
        ? chatStoreCore.currentChat?.last_seen_at
        : null,
      presenceNow.value,
    )

  if (role && presence) {
    return `${role} (${presence})`
  }

  return role || presence
})

const dialogHeaderName = computed(() => {
  const chatId = store.activeChat?.chat?.id
  const fallback = store.activeChat?.contact?.fullName || ''
  if (chatId == null) {
    return fallback
  }

  const coreChat = chatStoreCore.currentChat?.id === chatId
    ? chatStoreCore.currentChat
    : chatStoreCore.chats.find(chat => chat.id === chatId)

  return coreChat ? (resolveChatHeaderTitle(coreChat) || fallback) : fallback
})

const confirmDeleteChat = async () => {
  if (!store.activeChat?.chat) {
    return
  }

  modals.isDeletingChat.value = true

  try {
    const chatId = store.activeChat.chat.id
    const shouldDeleteForPeer = isActiveDialogChat.value && deleteForPeer.value

    await chatStoreCore.deleteChat(chatId, {
      deleteForPeer: shouldDeleteForPeer,
      destroy: !isActiveDialogChat.value,
    })

    store.activeChat = null
    await store.fetchChatsAndContacts(q.value)
    modals.closeDeleteChatDialog()

    feedbackStore.showToast({
      type: 'success',
      message: 'Чат удалён',
      time: 3000,
    })
  } catch (error) {
    debugConsoleError(error)
    feedbackStore.showToast({
      type: 'error',
      message: 'Не удалось удалить чат',
      time: 3000,
    })
  } finally {
    modals.isDeletingChat.value = false
  }
}

const cancelDeleteChat = () => {
  deleteForPeer.value = false
  modals.closeDeleteChatDialog()
}

const leaveGroup = () => {
  if (!store.activeChat?.chat || isEventChatActive.value || !store.canLeaveActiveChat()) {
    return
  }

  modals.openLeaveGroupDialog()
}

const confirmLeaveGroup = async () => {
  if (!store.activeChat?.chat) {
    return
  }

  modals.isLeavingGroup.value = true

  try {
    const chatId = store.activeChat.chat.id

    await chatStoreCore.deleteChat(chatId)

    store.activeChat = null
    await store.fetchChatsAndContacts(q.value)
    modals.closeLeaveGroupDialog()

    feedbackStore.showToast({
      type: 'success',
      message: 'Вы успешно покинули группу',
      time: 3000,
    })
  } catch (error) {
    debugConsoleError('Ошибка при выходе из группы:', error)
    feedbackStore.showToast({
      type: 'error',
      message: 'Не удалось покинуть группу',
      time: 3000,
    })
  } finally {
    modals.isLeavingGroup.value = false
  }
}

const cancelLeaveGroup = () => {
  modals.closeLeaveGroupDialog()
}

const leaveGroupDialogText = computed(() => {
  const groupTitle = (store.activeChat?.chat as { title?: string } | undefined)?.title
  if (groupTitle) {
    return `Вы покинете группу "${groupTitle}". Чтобы вернуться, вам потребуется новое приглашение.`
  }

  return 'Вы покинете эту группу. Чтобы вернуться, вам потребуется новое приглашение.'
})

const openFileUploadDialog = (files?: File[]) => {
  modals.openFileUploadDialog(files)
}

const onPasteFiles = (files: File[]) => {
  if (!files.length) return
  openFileUploadDialog(files)
}

const onConsumedInitialFiles = () => {
  modals.pendingUploadFiles.value = []
}

const onMessageSent = () => {
  replyTo.value = null
  store.syncWithCoreStore()
  nextTick(() => scrollToBottom({ behavior: 'auto' }))
}

const handleAvatarClick = async (userId: string | number) => {
  const userIdString = String(userId)

  if (!userIdString || userIdString === 'undefined' || userIdString === 'null') {
    debugConsoleError('Некорректный userId', { userId, userIdString })

    return
  }

  // В Matrix senderId / created_by.id — MXID (@user:server), API employee ждёт UUID
  let employeeId = userIdString
  if (userIdString.startsWith('@')) {
    const resolved = await chatStoreCore.resolveEmployeeIdByMatrixUserId(userIdString)
    if (!resolved) {
      useFeedbackStore().showToast({
        type: 'error',
        message: 'Не удалось найти сотрудника',
        time: 4000,
      })
      return
    }
    employeeId = resolved
  }

  modals.openEmployeeProfile(employeeId)
}

const handleProfileClick = async () => {
  if (!store.activeChat?.chat) {
    debugConsoleWarn('Нет activeChat')

    return
  }

  const uuid = await fetchEmployeeUuid()

  if (uuid) {
    modals.openEmployeeProfile(uuid)
  } else {
    debugConsoleWarn('Не удалось найти UUID сотрудника')
  }
}

const {
  isChatNotificationsEnabled,
  notificationButtonIcon,
} = useActiveChatNotifications(() => store.activeChat?.chat?.id)

const handleGroupCreated = async (chatId: number) => {
  await store.getChat(chatId)
}

const handleTopicCreated = async (title: string) => {
  await chatStoreCore.createTopic(title)
}

const handleDeleteTopic = async (topic: IChatTopic) => {
  if (topic.general) {
    return
  }

  const confirmed = confirm(
    `Удалить тему «${topic.title}»?\nСообщения темы станут недоступны.`,
  )
  if (!confirmed) {
    return
  }

  try {
    await chatStoreCore.archiveTopic(topic.id)
    useFeedbackStore().showToast({
      type: 'success',
      message: 'Тема удалена',
      time: 3000,
    })
  } catch (error) {
    debugConsoleError(error)
    useFeedbackStore().showToast({
      type: 'error',
      message: 'Не удалось удалить тему',
      time: 3000,
    })
  }
}

const handleTopicDialogSubmit = async (title: string) => {
  if (modals.topicDialogMode.value === 'rename') {
    const topic = modals.renamingTopic.value
    if (!topic) {
      return
    }
    try {
      await chatStoreCore.updateTopic(topic.id, title)
      useFeedbackStore().showToast({
        type: 'success',
        message: 'Тема переименована',
        time: 3000,
      })
    } catch (error) {
      debugConsoleError(error)
      useFeedbackStore().showToast({
        type: 'error',
        message: 'Не удалось переименовать тему',
        time: 3000,
      })
    }
    return
  }

  await handleTopicCreated(title)
}

const handleGroupUpdated = async () => {
  const chat = store.activeChat?.chat
  if (!chat?.id) return

  if ('matrix_room_id' in chat && (chat as IChat).matrix_room_id) {
    await store.fetchChatsAndContacts('', true)
    return
  }

  await store.getChat(chat.id)
}

const moreList = computed(() => {
  const list = []
  const isGroup = store.isGroupOrChannel()
  const isEvent = isEventChatActive.value
  const canManageGroup = store.canManageChatSettings()
  const activeChat = store.activeChat?.chat
  const chatId = activeChat?.id

  if (isEvent && activeEventChat.value?.event_url) {
    list.push({
      title: 'Открыть задачу',
      value: 'Open Event',
      prependIcon: 'ri-external-link-line',
      onClick: openEventUrl,
    })
  }

  if (isGroup && canManageGroup && chatId && !isEvent) {
    list.push({
      title: activeChat?.type === 'channel' ? 'Управление каналом' : 'Управление группой',
      value: 'Manage Group',
      prependIcon: 'ri-list-settings-line',
      onClick: () => modals.openManageGroupDialog(),
    })
  } else if (isGroup && !canManageGroup && chatId && !isEvent) {
    list.push({
      title: activeChat?.type === 'channel' ? 'Информация о канале' : 'Информация о группе',
      value: 'Group Info',
      prependIcon: 'ri-information-line',
      onClick: () => modals.openGroupInfoDialog(),
    })
  } else if (!isEvent && activeChat?.type !== 'favorite') {
    list.push({
      title: 'Просмотр контакта',
      value: 'View Contact',
      prependIcon: 'ri-user-line',
      onClick: async () => {
        if (!isGroupOrChannel.value) {
          const uuid = await fetchEmployeeUuid()

          if (uuid) {
            modals.openEmployeeProfile(uuid)
          } else {
            debugConsoleWarn('Не удалось найти UUID сотрудника')
          }
        }
      },
    })
  }

  // TODO: вернуть пункт меню «Ссылка-приглашение» (GROUP_INVITE_UI_ENABLED)
  if (GROUP_INVITE_UI_ENABLED && isGroup && chatId && !isEvent) {
    list.push({
      title: 'Ссылка-приглашение',
      value: 'Copy Invite Link',
      prependIcon: 'ri-link',
      onClick: () => copyGroupInviteLink(chatId),
    })
  }

  if (isGroup && chatId && !isEvent) {
    list.push({
      title: 'Копировать ссылку',
      value: 'Copy Group Link',
      prependIcon: 'ri-link-m',
      onClick: () => copyGroupShareLink(chatId),
    })
  }

  if (isGroup && !isEvent && store.canManageChatSettings()
    && chatStoreCore.currentChat?.capabilities?.can_enable_topics) {
    list.push({
      title: 'Включить темы',
      value: 'Enable Topics',
      prependIcon: 'ri-hashtag',
      onClick: () => chatStoreCore.enableTopicsForCurrentChat(),
    })
  }

  const topicsChat = chatStoreCore.currentChat
  if (
    isGroup
    && !isEvent
    && chatId
    && isTopicsModeChat(topicsChat)
    && sameActiveChat(chatId)
  ) {
    const tabs = resolveTopicsDisplayMode(topicsChat) === 'tabs'
    list.push({
      title: tabs ? 'Темы списком' : 'Темы вкладками',
      value: 'Toggle Topics Display',
      prependIcon: tabs ? 'ri-list-unordered' : 'ri-layout-top-line',
      onClick: () => applyTopicsDisplayMode(chatId, tabs ? 'list' : 'tabs'),
    })
  }

  list.push(
    {
      title: 'Файлы и медиа',
      value: 'Files and Media',
      prependIcon: 'ri-file-line',
      onClick: () => modals.openFilesAndMediaDialog(),
    },
    buildNotificationsMenuItem(isChatNotificationsEnabled, notificationButtonIcon),
  )

  if (isGroup && chatId && !isEvent && store.canLeaveActiveChat()) {
    list.push({
      title: 'Покинуть группу',
      value: 'Leave Group',
      prependIcon: 'ri-login-box-line',
      onClick: () => leaveGroup(),
    })
  }

  if ((!isGroup && !isEvent) || (isGroup && store.canDeleteActiveChat())) {
    list.push({
      title: 'Удалить чат',
      value: 'Delete Chat',
      prependIcon: 'ri-delete-bin-line',
      onClick: () => deleteChat(),
    })
  }

  return list
})

const headerImageLoadError = ref(false)

const handleHeaderImageError = () => {
  headerImageLoadError.value = true
}

const activeGroupChat = computed(() => {
  const chat = store.activeChat?.chat
  return chat && isGroupChat(chat) ? chat : null
})

const topicsTabsChat = computed(() => {
  const core = chatStoreCore.currentChat
  if (core && isTopicsTabsDisplay(core) && sameActiveChat(core.id)) {
    return core
  }

  const ui = activeGroupChat.value
  return ui && isTopicsTabsDisplay(ui as IChat) ? (ui as IChat) : null
})

const showTopicsTabs = computed(() => Boolean(topicsTabsChat.value))

const canDropFiles = () =>
  showComposer.value
  && (showTopicsTabs.value
    ? Boolean(chatStoreCore.activeTopic)
    : (!chatStoreCore.showTopicsList || Boolean(chatStoreCore.activeTopic)))

const {
  isDraggingFiles,
  onDragEnter,
  onDragOver,
  onDragLeave,
  onDrop,
} = useChatFileDrop({
  enabled: canDropFiles,
  onFiles: files => openFileUploadDialog(files),
})

const canManageTopicsTabs = computed(() =>
  Boolean(
    store.isCurrentUserAdmin()
    && topicsTabsChat.value?.capabilities?.can_manage_topics !== false,
  ),
)

function sameActiveChat(chatId: number | string | null | undefined): boolean {
  if (chatId == null) {
    return false
  }
  return String(store.activeChat?.chat?.id ?? '') === String(chatId)
}

/** Личный список/табы: localStorage + сразу переключаем UI (либо сайдбар, либо вкладки). */
function applyTopicsDisplayMode(chatId: number | string, mode: 'list' | 'tabs') {
  setStoredTopicsDisplayMode(chatId, mode)

  if (mode === 'tabs') {
    chatStoreCore.showTopicsList = false
    chatStoreCore.selectedTopicsGroupId = null
    if (!chatStoreCore.activeTopic) {
      const general = findGeneralTopic(chatStoreCore.currentChat)
      if (general?.matrix_room_id) {
        chatStoreCore.openTopic(general)
      }
    }
    return
  }

  chatStoreCore.showTopicsList = true
  chatStoreCore.selectedTopicsGroupId = chatId
}

function handleTopicTabSelect(topic: IChatTopic) {
  chatStoreCore.openTopic(topic)
}

/** Только confirmed total_members из currentChat — без fallback на UI (там часто ложная «1»). */
const activeChatMemberCount = computed(() => {
  const uiChat = store.activeChat?.chat
  if (!uiChat) {
    return 0
  }

  if (chatStoreCore.currentChat?.id === uiChat.id) {
    const total = chatStoreCore.currentChat.total_members
    return typeof total === 'number' && total > 0 ? total : 0
  }

  const listed = chatStoreCore.chats.find(c => c.id === uiChat.id)
  const total = listed?.total_members
  return typeof total === 'number' && total > 0 ? total : 0
})

const activeChatMembersLabel = computed(() => {
  const count = activeChatMemberCount.value
  if (count <= 0) {
    return ''
  }
  const word = count === 1
    ? 'участник'
    : count >= 2 && count <= 4
      ? 'участника'
      : 'участников'
  return `${count} ${word}`
})

const groupIconSource = computed(() => {
  if (!activeGroupChat.value) {
    return null
  }

  return activeGroupChat.value.icon
    || store.activeChat?.contact?.avatar
    || null
})

const groupMatrixRoomId = computed(() => {
  if (!activeGroupChat.value) {
    return null
  }

  return activeGroupChat.value.matrix_room_id || activeMatrixRoomId.value
})
const groupRoomAvatar = useGroupRoomAvatar(groupIconSource, groupMatrixRoomId)

const activeChatAvatar = computed(() => {
  if (!store.activeChat) {
    return null
  }

  if (activeGroupChat.value) {
    // Same URL as the sidebar list item — paint immediately, no null→letter flash.
    return groupRoomAvatar.value || groupIconSource.value || null
  }

  if (activeEventChat.value) {
    return activeEventChat.value.icon || null
  }

  return store.activeChat.contact.avatar || null
})

watch(activeChatAvatar, () => {
  headerImageLoadError.value = false
})

const shouldShowStatusBadge = computed(() =>
  !isGroupOrChannel.value
  && !isEventChatActive.value
  && store.activeChat?.contact?.online === true,
)

const activateMessageSearch = async () => {
  if (!store.activeChat) {
    return
  }

  isMessageSearchActive.value = true
  q.value = ''
  await nextTick()
  setTimeout(() => {
    const searchInput = document.querySelector('#search') as HTMLInputElement

    if (searchInput) {
      searchInput.focus()
      searchInput.select()
    }
  }, 200)
}

const deactivateMessageSearch = () => {
  isMessageSearchActive.value = false
  clearSearch()
}

const scrollToMessage = async (messageIndex: number | string, matrixEventId?: string) => {
  if (!chatLogPS.value || !store.activeChat) {
    debugConsoleWarn('[chat.vue] Не могу выполнить скролл: chatLogPS или activeChat отсутствует')
    return
  }

  const found = await scrollToChatMessage(chatLogPS.value, messageIndex, matrixEventId, {
    ensureEvent: async eventId => {
      await chatStoreCore.loadMessagesAroundEvent(eventId)
      store.syncWithCoreStore()
    },
    refetch: async () => {
      const chatId = store.activeChat?.chat?.id
      if (!chatId) {
        return
      }
      await chatStoreCore.fetchMessages(chatId, { mergeWithExisting: true })
      store.syncWithCoreStore()
    },
  })

  if (!found) {
    debugConsoleError('[chat.vue] Сообщение не найдено. messageIndex:', messageIndex)
    useFeedbackStore().showToast({
      type: 'info',
      message: 'Не удалось перейти к исходному сообщению',
      time: 3000,
    })
  }
}
</script>

<template>
  <MatrixAuthPrompt
    v-if="chatStoreCore.needsMatrixAuth"
    callback-path="/messenger"
  />

  <VLayout
    v-else
    class="chat-app-layout bg-surface"
    style="z-index: 0"
  >
    <VNavigationDrawer
      v-model="modals.isUserProfileSidebarOpen.value"
      data-allow-mismatch
      temporary
      touchless
      absolute
      class="user-profile-sidebar"
      location="start"
      width="370"
    >
      <ChatUserProfileSidebarContent @close="modals.closeUserProfile" />
    </VNavigationDrawer>

    <VNavigationDrawer
      v-if="vuetifyDisplays.mdAndUp.value"
      v-model="isLeftSidebarOpen"
      data-allow-mismatch
      absolute
      touchless
      location="start"
      width="370"
      permanent
      class="chat-list-sidebar"
    >
      <ChatLeftSidebarContent
        ref="leftSidebarSearchInputRef"
        v-model:is-drawer-open="isLeftSidebarOpen"
        v-model:search="q"
        :is-message-search-active="isMessageSearchActive"
        :message-search-results="messageSearchResults"
        @open-chat-of-contact="openChatOfContact"
        @show-user-profile="modals.openUserProfile"
        @close="closeLeftSidebar"
        @open-create-group-dialog="modals.openCreateGroupDialog"
        @open-create-topic-dialog="modals.openCreateTopicDialog"
        @open-rename-topic-dialog="modals.openRenameTopicDialog"
        @delete-topic="handleDeleteTopic"
        @deactivate-message-search="deactivateMessageSearch"
        @scroll-to-message="scrollToMessage"
      />
    </VNavigationDrawer>

    <VMain class="chat-content-container">
      <ChatLeftSidebarContent
        v-if="isMobileChatListMode"
        ref="leftSidebarSearchInputRef"
        v-model:is-drawer-open="isLeftSidebarOpen"
        v-model:search="q"
        embedded-list
        class="mobile-chat-list-panel h-100"
        :is-message-search-active="isMessageSearchActive"
        :message-search-results="messageSearchResults"
        @open-chat-of-contact="openChatOfContact"
        @show-user-profile="modals.openUserProfile"
        @open-create-group-dialog="modals.openCreateGroupDialog"
        @open-create-topic-dialog="modals.openCreateTopicDialog"
        @open-rename-topic-dialog="modals.openRenameTopicDialog"
        @delete-topic="handleDeleteTopic"
        @deactivate-message-search="deactivateMessageSearch"
        @scroll-to-message="scrollToMessage"
      />

      <div
        v-else-if="store.activeChat"
        class="chat-panel d-flex flex-column h-100"
        :class="{ 'chat-panel--drag-over': isDraggingFiles }"
        @dragenter="onDragEnter"
        @dragover="onDragOver"
        @dragleave="onDragLeave"
        @drop="onDrop"
      >
        <div
          v-if="isDraggingFiles"
          class="chat-panel-drop-hint"
        >
          Отпустите файл сюда
        </div>
        <div class="active-chat-header d-flex align-center text-medium-emphasis">
          <IconBtn
            class="d-md-none me-4"
            @click="backToMobileChatList"
          >
            <VIcon icon="ri-arrow-left-line" />
          </IconBtn>

          <div
            v-if="store.activeChat && activeGroupChat"
            :key="`chat-header-group-${activeGroupChat.id || 'none'}-${chatStoreCore.activeTopic?.id || 'topics'}`"
            class="d-flex align-center flex-grow-1 overflow-hidden"
          >
            <VAvatar
              size="40"
              :variant="!activeChatAvatar || headerImageLoadError ? 'tonal' : undefined"
              :color="(!activeChatAvatar || headerImageLoadError) ? 'primary' : undefined"
              :class="(!activeChatAvatar || headerImageLoadError) ? getAvatarColor(activeGroupChat.title || '') : []"
              class="me-4"
            >
              <AuthenticatedVImg
                v-if="activeChatAvatar && !headerImageLoadError"
                :src="activeChatAvatar"
                :alt="activeGroupChat.title"
                @error="handleHeaderImageError"
              />
              <span v-else>{{ avatarText(activeGroupChat.title || '') }}</span>
            </VAvatar>
            <div class="flex-grow-1 overflow-hidden">
              <h6 class="text-h6 font-weight-regular text-truncate">
                <template v-if="chatStoreCore.activeTopic && !showTopicsTabs">
                  {{ chatStoreCore.activeTopic.title }}
                </template>
                <template v-else-if="chatStoreCore.showTopicsList && !showTopicsTabs">
                  {{ activeGroupChat.title }}
                </template>
                <template v-else>
                  {{ groupChatTypeLabel(activeGroupChat.type) }} - {{ activeGroupChat.title }}
                </template>
              </h6>
              <p
                v-if="typingText && !chatStoreCore.showTopicsList"
                class="text-body-2 text-primary text-truncate mb-0"
              >
                {{ typingText }}
              </p>
              <p
                v-else-if="activeChatMembersLabel"
                class="text-body-2 text-truncate mb-0"
              >
                {{ activeChatMembersLabel }}
              </p>
            </div>
          </div>
          <div
            v-else-if="store.activeChat && activeEventChat"
            :key="`chat-header-event-${activeEventChat.id || 'none'}`"
            class="d-flex align-center flex-grow-1 overflow-hidden"
          >
            <VAvatar
              size="40"
              :variant="!activeChatAvatar || headerImageLoadError ? 'tonal' : undefined"
              :color="(!activeChatAvatar || headerImageLoadError) ? 'primary' : undefined"
              :class="(!activeChatAvatar || headerImageLoadError) ? getAvatarColor(activeEventChat.title || '') : []"
              class="me-4"
            >
              <AuthenticatedVImg
                v-if="activeChatAvatar && !headerImageLoadError"
                :src="activeChatAvatar"
                :alt="activeEventChat.title"
                @error="handleHeaderImageError"
              />
              <span v-else>{{ avatarText(activeEventChat.title || '') }}</span>
            </VAvatar>
            <div class="flex-grow-1 overflow-hidden">
              <h6 class="text-h6 font-weight-regular text-truncate">
                {{ activeEventChat.title }}
              </h6>
              <p
                v-if="typingText"
                class="text-body-2 text-primary text-truncate mb-0"
              >
                {{ typingText }}
              </p>
              <p
                v-else-if="activeChatMembersLabel"
                class="text-body-2 text-truncate mb-0"
              >
                {{ activeChatMembersLabel }}
              </p>
            </div>
          </div>
          <div
            v-else-if="store.activeChat && store.activeChat.contact"
            :key="`chat-header-${store.activeChat?.chat?.id || 'none'}`"
            class="d-flex align-center cursor-pointer"
            @click="handleProfileClick"
          >
            <VBadge
              v-if="shouldShowStatusBadge"
              dot
              location="bottom right"
              offset-x="3"
              offset-y="3"
              :color="
                resolveAvatarBadgeVariant(store.activeChat.contact.online)
              "
              bordered
              class="me-4"
            >
              <VAvatar
                size="40"
                :variant="
                  !activeChatAvatar || headerImageLoadError ? 'tonal' : undefined
                "
                :color="
                  !activeChatAvatar || headerImageLoadError
                    ? resolveAvatarBadgeVariant(store.activeChat.contact.online)
                    : undefined
                "
                :class="[
                  (!activeChatAvatar || headerImageLoadError)
                    ? getAvatarColor(dialogHeaderName)
                    : '',
                ]"
                class="cursor-pointer"
              >
                <VImg
                  v-if="activeChatAvatar && !headerImageLoadError"
                  :src="activeChatAvatar"
                  :alt="dialogHeaderName"
                  @error="handleHeaderImageError"
                />
                <span v-else>{{
                  avatarText(dialogHeaderName)
                }}</span>
              </VAvatar>
            </VBadge>
            <template v-else>
              <VAvatar
                size="40"
                :variant="
                  !activeChatAvatar || headerImageLoadError ? 'tonal' : undefined
                "
                color="primary"
                :class="[
                  (!activeChatAvatar || headerImageLoadError)
                    ? getAvatarColor(dialogHeaderName)
                    : '',
                ]"
                class="cursor-pointer me-4"
              >
                <VImg
                  v-if="activeChatAvatar && !headerImageLoadError"
                  :src="activeChatAvatar"
                  :alt="dialogHeaderName"
                  @error="handleHeaderImageError"
                />
                <span v-else>{{
                  avatarText(dialogHeaderName)
                }}</span>
              </VAvatar>
            </template>

            <div class="flex-grow-1 overflow-hidden">
              <h6 class="text-h6 font-weight-regular">
                {{ dialogHeaderName }}
              </h6>
              <p class="text-body-2 text-truncate mb-0">
                <span :class="{ 'text-primary': Boolean(typingText) || store.activeChat.contact.online }">
                  {{ dialogStatusLabel }}
                </span>
              </p>
            </div>
          </div>

          <VSpacer />

          <div
            v-if="isEventChatActive && activeEventChat?.event_url"
            class="event-chat-header-actions d-flex align-center gap-2 me-2 flex-shrink-0"
          >
            <VBtn
              variant="tonal"
              color="primary"
              size="small"
              prepend-icon="ri-external-link-line"
              @click="openEventUrl"
            >
              Открыть задачу
            </VBtn>
          </div>

          <div class="d-sm-flex align-center d-none">
            <VTooltip
              location="top"
              text="Поиск по чату"
            >
              <template #activator="{ props }">
                <IconBtn
                  v-bind="props"
                  @click="activateMessageSearch"
                >
                  <VIcon icon="ri-search-line" />
                </IconBtn>
              </template>
            </VTooltip>
          </div>

          <MoreBtn :menu-list="moreList" />
        </div>

        <ChatTopicsTabs
          v-if="showTopicsTabs && topicsTabsChat"
          :chat="topicsTabsChat"
          :active-topic-id="chatStoreCore.activeTopic?.id"
          :can-manage="canManageTopicsTabs"
          @select="handleTopicTabSelect"
          @create="modals.openCreateTopicDialog"
          @rename="modals.openRenameTopicDialog"
          @delete="handleDeleteTopic"
        />

        <VDivider v-if="!showTopicsTabs" />

        <div
          ref="chatLogPS"
          class="flex-grow-1 chat-messages-scroll"
          @scroll.passive="onChatLogScroll"
        >
          <div
            v-if="isComposerBlocked && !showTimeline && readyStatus === 'failed'"
            class="d-flex flex-column align-center justify-center h-100 text-medium-emphasis text-body-2 px-4 text-center"
          >
            {{ readyMessage || 'Не удалось подключиться' }}
            <VBtn
              v-if="canRetryConnection"
              class="mt-3"
              variant="tonal"
              :loading="retrying"
              @click="retryConnection"
            >
              Повторить подключение
            </VBtn>
          </div>
          <div
            v-else-if="isComposerBlocked && !showTimeline && readyMessage !== 'Выберите тему'"
            class="chat-log pa-5"
          >
            <ChatLogSkeleton />
          </div>
          <div
            v-else-if="showTopicsTabs && !chatStoreCore.activeTopic"
            class="d-flex flex-column align-center justify-center h-100 text-medium-emphasis px-4 text-center"
          >
            <VIcon
              icon="ri-chat-thread-line"
              size="48"
              class="mb-3"
            />
            <div class="text-body-1 mb-1">
              Подготовка темы…
            </div>
            <div class="text-body-2">
              Тема ещё создаётся, подождите немного
            </div>
          </div>
          <div
            v-else-if="chatStoreCore.showTopicsList && !chatStoreCore.activeTopic"
            class="d-flex flex-column align-center justify-center h-100 text-medium-emphasis px-4 text-center"
          >
            <VIcon
              icon="ri-chat-thread-line"
              size="48"
              class="mb-3"
            />
            <div class="text-body-1 mb-1">
              Выберите тему
            </div>
            <div class="text-body-2">
              Откройте тему в списке слева, чтобы начать обсуждение
            </div>
          </div>
          <ChatLog
            v-else
            ref="chatLogRef"
            :loading-older-history="isLoadingOlderHistory"
            @reply="handleReply"
            @quote-click="scrollToMessage"
            @avatar-click="handleAvatarClick"
            @start-edit="handleStartEdit"
            @selection-change="onMessageSelectionChange"
          />
        </div>

        <MessageSelectionBar
          v-if="isMessageSelecting"
          :count="selectedMessageCount"
          @cancel="chatLogRef?.clearMessageSelection()"
          @forward="chatLogRef?.openForwardSelected()"
        />

        <ChatInput
          v-else-if="showComposer && (showTopicsTabs ? Boolean(chatStoreCore.activeTopic) : (!chatStoreCore.showTopicsList || chatStoreCore.activeTopic))"
          ref="messageInputRef"
          v-model="msg"
          :editing-message="editingMessage"
          :reply-to="replyTo"
          :members="mentionMembers"
          :exclude-mention-key="excludeMentionKey"
          :ensure-members="ensureMentionMembers"
          @send="sendMessage"
          @cancel-edit="cancelEdit"
          @cancel-reply="cancelReply"
          @paste-files="onPasteFiles"
        >
          <template #actions>
            <IconBtn
              size="small"
              style="display: none"
            >
              <VIcon
                icon="ri-mic-line"
                class="text-high-emphasis"
              />
            </IconBtn>
            <IconBtn
              ref="emojiBtnRef"
              size="small"
              class="emoji-picker-btn"
              @click="emojiPicker.toggle"
            >
              <VIcon
                icon="ri-emotion-happy-line"
                class="text-high-emphasis"
              />
            </IconBtn>
            <IconBtn
              size="small"
              @click="openFileUploadDialog"
            >
              <VIcon
                icon="ri-attachment-2"
                class="text-high-emphasis"
              />
            </IconBtn>
          </template>
        </ChatInput>

        <input
          ref="refInputEl"
          type="file"
          name="file"
          accept=".jpeg,.png,.jpg,.gif"
          hidden
        >

        <Teleport to="body">
          <div
            v-if="emojiPicker.showEmojiPicker.value"
            :ref="(el) => (emojiPicker.emojiPanelRef.value = el as HTMLElement)"
            class="emoji-picker-overlay"
            :style="{
              ...(vuetifyDisplays.smAndDown.value
                && emojiPicker.emojiPickerPosition.value.bottom !== undefined
                ? {
                  bottom: `${emojiPicker.emojiPickerPosition.value.bottom}px`,
                  left: `${emojiPicker.emojiPickerPosition.value.left}px`,
                }
                : {
                  top: `${emojiPicker.emojiPickerPosition.value.top}px`,
                  left: `${emojiPicker.emojiPickerPosition.value.left}px`,
                }),
              visibility: emojiPicker.isPositioned.value ? 'visible' : 'hidden',
            }"
            @mouseenter="emojiPicker.onPanelEnter"
            @mouseleave="emojiPicker.onPanelLeave"
          >
            <EmojiPicker
              native
              :group-names="emojiPicker.emojiGroupNamesRu"
              :static-texts="emojiPicker.emojiStaticTextsRu"
              @select="selectEmoji"
            />
          </div>
        </Teleport>
      </div>

      <ChatPanelSkeleton v-else-if="showChatPanelSkeleton" />

      <!-- Пустое состояние — только desktop; на мобилке список в VMain -->
      <div
        v-else-if="!vuetifyDisplays.smAndDown.value"
        class="d-flex flex-column align-center justify-center h-100 text-center pa-4 empty-chat-state"
      >
        <div class="empty-chat-icon-wrapper mb-6">
          <VIcon
            icon="ri-wechat-line"
            size="64"
            class="empty-chat-icon"
          />
        </div>
        <p class="text-body-1 text-medium-emphasis">
          Начните общение,<br>выбрав контакт слева
        </p>
      </div>
    </VMain>

    <ConfirmChatDialog
      v-model="modals.isDeleteChatDialogOpen.value"
      title="Вы уверены, что хотите удалить этот чат?"
      text="Все сообщения будут удалены без возможности восстановления"
      confirm-text="Удалить"
      max-width="532"
      :loading="modals.isDeletingChat.value"
      @confirm="confirmDeleteChat"
      @cancel="cancelDeleteChat"
    >
      <VCardText
        v-if="isActiveDialogChat && store.activeChat?.contact?.fullName"
        class="pt-0"
      >
        <VCheckbox
          v-model="deleteForPeer"
          :label="`Также удалить у ${store.activeChat?.contact?.fullName}?`"
          hide-details
        />
      </VCardText>
    </ConfirmChatDialog>

    <ConfirmChatDialog
      v-model="modals.isLeaveGroupDialogOpen.value"
      title="Вы уверены, что хотите покинуть группу?"
      :text="leaveGroupDialogText"
      confirm-text="Покинуть группу"
      max-width="532"
      :loading="modals.isLeavingGroup.value"
      @confirm="confirmLeaveGroup"
      @cancel="cancelLeaveGroup"
    />

    <FilesAndMediaDialog v-model="modals.isFilesAndMediaDialogOpen.value" />

    <FileUploadDialog
      v-model="modals.isFileUploadDialogOpen.value"
      :initial-files="modals.pendingUploadFiles.value"
      :reply-to="replyTo"
      @message-sent="onMessageSent"
      @consumed-initial-files="onConsumedInitialFiles"
    />

    <CreateGroupDialog
      v-model="modals.isCreateGroupDialogOpen.value"
      @group-created="handleGroupCreated"
    />

    <CreateTopicDialog
      v-model="modals.isCreateTopicDialogOpen.value"
      :mode="modals.topicDialogMode.value"
      :initial-title="modals.renamingTopic.value?.title || ''"
      @submit="handleTopicDialogSubmit"
    />

    <ManageGroupDialog
      v-model="modals.isManageGroupDialogOpen.value"
      :group-id="store.activeChat?.chat?.id"
      @group-updated="handleGroupUpdated"
    />

    <GroupInfoDialog
      v-model="modals.isGroupInfoDialogOpen.value"
      :group-id="store.activeChat?.chat?.id"
    />

    <EmployeeProfileSidebar
      v-model="modals.isEmployeeProfileOpen.value"
      :employee-id="modals.employeeId.value"
    />
  </VLayout>
</template>

<style lang="scss">
$chat-app-header-height: 76px;

@media (max-width: 768px) {
  .layout-page-content {
    height: calc(100dvh - 65px) !important;
  }
}

%chat-header {
  display: flex;
  align-items: center;
  min-block-size: $chat-app-header-height;
  padding-inline: 1.25rem;
}

.chat-app-layout {
  height: calc(100vh - 100px);
  max-height: calc(100vh - 100px);
  overflow: hidden;
  border-radius: 6px;
  box-shadow: 0 4px 10px rgba(46, 38, 61, 0.2);

  @media (max-width: 959px) {
    height: calc(100vh - 155px);
    max-height: calc(100vh - 155px);
  }

  .chat-list-header,
  .active-chat-header {
    @extend %chat-header;
  }

  .active-chat-header {
    background-color: rgb(var(--v-theme-background));
  }

  .chat-panel,
  .chat-messages-scroll,
  .chat-log-message-form {
    background-color: rgb(var(--v-theme-background));
  }

  .chat-panel {
    position: relative;
  }

  .chat-panel--drag-over {
    outline: 2px solid rgb(var(--v-theme-primary));
    outline-offset: -2px;
  }

  .chat-panel-drop-hint {
    position: absolute;
    inset: 0;
    z-index: 5;
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: none;
    font-size: 0.9375rem;
    font-weight: 500;
    color: rgb(var(--v-theme-primary));
    background: rgba(var(--v-theme-surface), 0.88);
  }

  .active-chat-user-profile-sidebar,
  .user-profile-sidebar {
    .v-navigation-drawer__content {
      display: flex;
      flex-direction: column;
    }
  }

  .chat-list-search {
    .v-field__outline__start {
      flex-basis: 20px !important;
      border-radius: 28px 0 0 28px !important;
    }

    .v-field__outline__end {
      border-radius: 0 28px 28px 0 !important;
    }
  }

  .chat-list-sidebar {
    .v-navigation-drawer__content {
      display: flex;
      flex-direction: column;
      block-size: 100%;
      overflow: hidden;
    }
  }

  .chat-content-container {
    background-color: rgb(var(--v-theme-background)) !important;
    height: 100%;
    max-height: 100%;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }

  .mobile-chat-list-panel {
    display: flex;
    flex-direction: column;
    min-block-size: 0;
    overflow: hidden;
  }

  .chat-log-message-form {
    flex-shrink: 0;

    .chat-input-field-wrap {
      position: relative;
    }

    .chat-message-input {
      .v-field__append-inner {
        align-items: end;
        padding-block: 8px;
      }

      .v-field--appended {
        padding-inline-end: 6px;
      }
    }

    .chat-send-btn {
      background-color: #24abaf !important;
      background: #24abaf !important;
      color: #ffffff !important;

      .v-btn__overlay {
        background-color: transparent !important;
      }

      .v-btn__content {
        color: #ffffff !important;
      }

      .v-icon {
        color: #ffffff !important;
      }

      &:hover {
        background-color: #1b8083 !important;
        background: #1b8083 !important;

        .v-btn__overlay {
          background-color: transparent !important;
        }
      }

      &.bg-primary {
        background-color: #24abaf !important;
        background: #24abaf !important;
      }

      &.v-btn--variant-elevated {
        background-color: #24abaf !important;
        background: #24abaf !important;
      }
    }

    .chat-send-icon-btn {
      background-color: #24abaf !important;
      background: #24abaf !important;
      color: #ffffff !important;

      .v-btn__content {
        margin-top: 3px;
        margin-right: 4px;
      }

      .v-icon {
        color: #ffffff !important;
      }

      &:hover {
        background-color: #1b8083 !important;
        background: #1b8083 !important;
      }
    }
  }

  .chat-messages-scroll {
    height: 100%;
    overflow-y: auto;
    overflow-x: hidden;
    scroll-behavior: auto;

    &.chat-scrolling {
      scroll-behavior: auto !important;
    }
  }

  .v-avatar {
    span {
      font-weight: 600;
      font-size: 0.875rem;
      color: #fff;
      text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
    }

    &.avatar-color-1 {
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    }

    &.avatar-color-2 {
      background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
    }

    &.avatar-color-3 {
      background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
    }

    &.avatar-color-4 {
      background: linear-gradient(135deg, #43e97b 0%, #38f9d7 100%);
    }

    &.avatar-color-5 {
      background: linear-gradient(135deg, #fa709a 0%, #fee140 100%);
    }

    &.avatar-color-6 {
      background: linear-gradient(135deg, #a8edea 0%, #fed6e3 100%);
    }

    &.avatar-color-7 {
      background: linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%);
    }

    &.avatar-color-8 {
      background: linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%);
    }
  }
}

@keyframes highlightMessage {
  0% {
    background-color: rgba(var(--v-theme-primary), 0.3);
  }
  100% {
    background-color: rgba(var(--v-theme-primary), 0.1);
  }
}

.emoji-picker-overlay {
  position: fixed;
  z-index: 9999;
  border-radius: 8px;
  background: transparent !important;

  .v-theme--dark & {
    background: transparent !important;
  }

  @media (max-width: 959px) {
    width: calc(100vw - 16px) !important;
    max-width: calc(100vw - 16px) !important;
    left: 8px !important;
    right: 8px !important;
    margin: 0 auto;
  }
}

.reply-context {
  padding: 8px 12px;
  background: rgba(var(--v-theme-surface), 0.8);
  border-radius: 8px;
  border: 1px solid rgba(var(--v-theme-outline), 0.12);
}

.reply-context-inner {
  display: flex;
  gap: 8px;
  align-items: flex-start;
}

.reply-context-line {
  width: 3px;
  background: rgb(var(--v-theme-primary));
  border-radius: 2px;
  flex-shrink: 0;
}

.reply-context-content {
  flex: 1;
  min-width: 0;
  overflow: hidden;
}

.reply-context-sender {
  font-size: 13px;
  font-weight: 600;
  color: rgb(var(--v-theme-primary));
  margin-bottom: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.reply-context-text {
  font-size: 12px;
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  line-height: 1.4;
}

.edit-context-in-input {
  width: 100%;
  background: rgb(var(--v-theme-surface));
  border-radius: 8px;
  border: 1px solid rgba(var(--v-theme-outline), 0.12);
}

.reply-context-in-input {
  width: 100%;
  padding: 12px 16px;
  background: rgb(var(--v-theme-surface));
  border-radius: 8px;
  border: 1px solid rgba(var(--v-theme-outline), 0.12);
  margin-bottom: 8px;
  box-shadow: 0 1px 3px rgba(var(--v-theme-on-surface), 0.05);
}

.reply-context-in-input .reply-context-inner {
  display: flex;
  gap: 12px;
  align-items: flex-start;
}

.reply-context-in-input .reply-context-quote-icon {
  color: rgb(var(--v-theme-primary));
  flex-shrink: 0;
  margin-top: 2px;
}

.reply-context-in-input .reply-context-content {
  flex: 1;
  min-width: 0;
  overflow: hidden;
}

.reply-context-in-input .reply-context-sender {
  font-size: 14px;
  font-weight: 500;
  color: rgb(var(--v-theme-primary));
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.4;
}

.reply-context-in-input .reply-context-text {
  font-size: 13px;
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  line-height: 1.4;
}

.reply-context-in-input .reply-context-close-btn {
  flex-shrink: 0;
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
  opacity: 0.7;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 1;
  }
}

.empty-chat-state {
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
}

.empty-chat-icon-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background-color: rgba(20, 184, 166, 0.1); /* teal-500 с прозрачностью */
}

.empty-chat-icon {
  color: rgb(20, 184, 166); /* teal-500 */
}

.v-theme--dark {
  .empty-chat-icon-wrapper {
    background-color: rgba(20, 184, 166, 0.15);
  }

  .empty-chat-icon {
    color: rgb(20, 184, 166);
  }
}
</style>
